import { useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ArrowLeftIcon, ExternalLinkIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import { useFetch } from '@/hooks/useFecth';
import server from '@/services/server';
import ClubAddContacts from '../components/ClubAddContacts';
import ClubEmptyList from '../components/ClubEmptyList';
import { useClub } from '../ClubContext';
import useClubErrors from '../hooks/useClubErrors';

const STATUS_CONFIG = {
    PENDING: { label: 'Pendente', className: 'border-amber-200 bg-amber-50 text-amber-800' },
    PROCESSING: { label: 'Processando', className: 'border-sky-200 bg-sky-50 text-sky-800' },
    CONCLUDED: { label: 'Concluído', className: '' },
};

function formatImportDate(dateStr) {
    if (!dateStr) return '—';
    try {
        return format(new Date(dateStr), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch {
        return '—';
    }
}

function ImportStatusBadge({ status }) {
    const config = STATUS_CONFIG[status] || { label: status || '—', className: '' };

    return (
        <Badge variant={status === 'CONCLUDED' ? 'default' : 'outline'} className={cn(config.className)}>
            {config.label}
        </Badge>
    );
}

export default function ListImports() {
    const [loading, setLoading] = useState(false);
    const { setErrors, enqueueSnackbar } = useClubErrors();
    const { club: clubData } = useClub();

    const { data, isFetching, mutate } = useFetch('club/subscribers/import', {
        params: {
            all: true,
            ...(clubData?.id ? { clubId: clubData.id } : {}),
        },
    });

    const imports = Array.isArray(data) ? data : [];

    const handleImport = async ({ file }) => {
        setLoading(true);
        const formData = new FormData();
        formData.append('file', file);
        if (clubData?.id) {
            formData.append('clubId', clubData.id);
        }

        try {
            const response = await server.post('club/subscribers/import', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            const summary = response?.data?.data || null;
            const errors = Number(summary?.errors) || 0;
            const ready = Number(summary?.ready ?? summary?.accepted) || 0;
            enqueueSnackbar(
                errors
                    ? `Importação: ${ready} na fila, ${errors} erro(s). Acompanhe os contadores no dialog.`
                    : 'Importação iniciada. Acompanhe o progresso no dialog.',
            );
            return summary;
        } catch (error) {
            setErrors(error);
            enqueueSnackbar('Erro ao importar contatos.', { variant: 'error' });
            return null;
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex flex-col gap-6">
            <Card>
                <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-4 space-y-0">
                    <div>
                        <CardTitle>Importações</CardTitle>
                        <CardDescription>Histórico de importações de assinantes.</CardDescription>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <ClubAddContacts
                            onImport={handleImport}
                            loading={loading}
                            clubData={clubData}
                            onClose={() => mutate()}
                        />
                        <Button variant="outline" size="sm" render={<Link to="../assinantes" />}>
                            <ArrowLeftIcon className="size-4" />
                            Voltar para assinantes
                        </Button>
                    </div>
                </CardHeader>
            </Card>

            <Card>
                <CardContent className="p-0">
                    {isFetching && !imports.length ? (
                        <div className="flex items-center justify-center gap-2 py-12">
                            <Spinner className="size-5" />
                            <span className="text-sm text-muted-foreground">Carregando…</span>
                        </div>
                    ) : null}

                    {!isFetching && !imports.length ? (
                        <ClubEmptyList
                            title="Nenhuma importação encontrada"
                            description="Importe assinantes usando o botão acima."
                        />
                    ) : null}

                    {imports.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b bg-muted/40 text-left">
                                        <th className="px-4 py-3 font-medium">Data</th>
                                        <th className="px-4 py-3 font-medium">Status</th>
                                        <th className="px-4 py-3 text-right font-medium">Total</th>
                                        <th className="px-4 py-3 text-right font-medium">Processados</th>
                                        <th className="px-4 py-3 text-right font-medium">Sucesso</th>
                                        <th className="px-4 py-3 text-right font-medium">Erros</th>
                                        <th className="px-4 py-3 font-medium">Planilha de erros</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {imports.map(row => (
                                        <tr key={row.id} className="border-b last:border-b-0">
                                            <td className="px-4 py-3 text-muted-foreground">{formatImportDate(row.createdAt)}</td>
                                            <td className="px-4 py-3">
                                                <ImportStatusBadge status={row.status} />
                                            </td>
                                            <td className="px-4 py-3 text-right">{row.countTotal ?? '—'}</td>
                                            <td className="px-4 py-3 text-right">{row.countProcessed ?? '—'}</td>
                                            <td className="px-4 py-3 text-right">{row.countSuccess ?? '—'}</td>
                                            <td className="px-4 py-3 text-right">{row.countError ?? '—'}</td>
                                            <td className="px-4 py-3">
                                                {row.url ? (
                                                    <Button
                                                        variant="link"
                                                        size="sm"
                                                        className="h-auto gap-1 p-0"
                                                        render={
                                                            <a href={row.url} target="_blank" rel="noopener noreferrer" />
                                                        }
                                                    >
                                                        <ExternalLinkIcon className="size-4" />
                                                        Baixar
                                                    </Button>
                                                ) : (
                                                    <span className="text-muted-foreground">—</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : null}
                </CardContent>
            </Card>
        </div>
    );
}
